/**
 * Shown when the user has been idle long enough to be near the auto sign-out
 * threshold. Any mouse / keyboard activity dismisses it automatically via
 * the idle-timeout hook's activity listener; the explicit "Stay signed in"
 * button is a fast path for click-through.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Clock } from 'lucide-react';

interface IdleWarningModalProps {
  visible: boolean;
  msUntilSignOut: number;
  onStayActive: () => void;
}

function formatRemaining(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
}

export function IdleWarningModal({ visible, msUntilSignOut, onStayActive }: IdleWarningModalProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Don't trap clicks: any click anywhere outside the modal also
          // counts as activity and dismisses naturally. This element is just
          // a visual layer.
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-950/80 backdrop-blur-sm pointer-events-none"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="w-full max-w-md bg-dark-900 border border-amber-500/40 rounded-2xl shadow-2xl p-6 pointer-events-auto"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5 text-amber-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Are you still there?</h2>
                <p className="text-sm text-dark-300 mt-1">
                  For your security, you&apos;ll be signed out automatically due to inactivity.
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-dark-800/80 border border-dark-700 px-4 py-3 flex items-center gap-3">
              <Clock className="h-4 w-4 text-amber-300" />
              <span className="text-sm text-dark-200">
                Signing out in <span className="font-semibold text-amber-300">{formatRemaining(msUntilSignOut)}</span>
              </span>
            </div>

            <p className="text-xs text-dark-500 mt-4">
              Move your mouse or press any key to stay signed in. You can also click below.
            </p>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={onStayActive}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyber-400 hover:bg-cyber-300 text-dark-950 text-sm font-semibold"
              >
                Stay signed in
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
